import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "@emotion/styled";
import { Skeleton } from "antd5";
import { capitalize } from "lodash";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { routes } from "lib/routes";
import { TrackingProvider } from "lib/tracking";
import { pluralise } from "lib/utils";
import CentredSpinner from "../../../../lib/core_components/CentredSpinner";
import { useAddOpportunityEntity } from "../../../../lib/hooks/api/opportunities/useAddOpportunityEntity";
import { OpportunityFromSearch } from "../../../../lib/hooks/api/opportunities/useOpportunities";
import { useRemoveOpportunityEntity } from "../../../../lib/hooks/api/opportunities/useRemoveOpportunityEntity";
import UIcon from "../../../../lib/icons/untitled_ui/UIcon";
import { OpportunityEntityType } from "../../../../lib/types/graphQLEnums";
import { smoothTransition } from "../../../../styles/animations";
import { Flex, Text } from "../../../../styles/utility-components";
import EmptyState from "../../../ui/empty_state/EmptyState";
import { ToastAction } from "../ToastAction";
import { convertEntityTypeToOpportunityEntityType, EntityType } from "../types";
import { LIST_CONTAINER_ID, MAX_MATCHING_BUYERS } from "./SaveToOpportunity";

export type OpportunityWithSavedStatus = OpportunityFromSearch & { isSaved: boolean };

export interface OpportunitiesWithTotal {
  opportunities: OpportunityWithSavedStatus[];
  total: number;
}

export function SaveToOpportunityList({
  isLoading,
  isError,
  opportunities: { opportunities, total },
  buyerIds,
  entityIds,
  entityType,
  fetchNextPage,
  hasNextPage,
  onSave,
}: {
  isLoading: boolean;
  isError: boolean;
  opportunities: OpportunitiesWithTotal;
  buyerIds: string[] | undefined;
  entityIds: string[];
  entityType: EntityType;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  onSave?: () => void;
}) {
  if (isLoading) {
    return <Skeleton active />;
  }

  if (isError) {
    return (
      <EmptyState
        type="loadError"
        heading="Sorry something has gone wrong loading opportunities"
        description="If this error persists please contact support"
        width="100%"
        size="large"
      />
    );
  }

  if (opportunities.length === 0) {
    return (
      <EmptyState
        heading="You haven't created any opportunities yet"
        description="Create opportunities once you identify leads from Stotles, which can then be tracked and progressed to our public sector pipeline."
        width="100%"
        size="large"
      />
    );
  }

  const opportunitiesWithBuyer = opportunities.filter((opp) =>
    opp.buyers.some((buyer) => buyerIds?.includes(buyer.id)),
  );
  const opportunitiesWithoutBuyer = opportunities.filter(
    (opp) => !opportunitiesWithBuyer.some((buyerOpp) => buyerOpp.id === opp.id),
  );

  const renderOpportunityList = (opportunityList: OpportunityWithSavedStatus[]) => (
    <>
      {opportunityList.map((opportunity) => (
        <TrackingProvider
          key={opportunity.id}
          data={{
            "Opportunity name": opportunity.name,
            "Opportunity stage": opportunity.stage.name,
            "Opportunity value": opportunity.value,
          }}
        >
          <OpportunityListItem
            opportunity={opportunity}
            entityIds={entityIds}
            entityType={entityType}
            onSave={onSave}
          />
        </TrackingProvider>
      ))}
    </>
  );

  const renderMatchingBuyerText = () => {
    if (opportunitiesWithBuyer.length >= MAX_MATCHING_BUYERS && hasNextPage) {
      return `${opportunitiesWithBuyer.length}+ Opportunities identified with same buyer`;
    }

    return `${opportunitiesWithBuyer.length} Opportunities identified with same buyer`;
  };

  return (
    <OpportunitiesListContainer
      dataLength={opportunities.length}
      next={fetchNextPage}
      hasMore={hasNextPage || false}
      loader={<CentredSpinner />}
      scrollableTarget={LIST_CONTAINER_ID}
    >
      {opportunitiesWithBuyer.length > 0 && (
        <MatchingBuyerOpportunitiesSection>
          <Text color="sysPrimaryPressed">{renderMatchingBuyerText()}</Text>
          {renderOpportunityList(opportunitiesWithBuyer)}
        </MatchingBuyerOpportunitiesSection>
      )}

      {opportunitiesWithoutBuyer.length > 0 && (
        <OtherOpportunitiesSection>
          <Text color="sysTextSecondary">
            {total - opportunitiesWithBuyer.length} {opportunitiesWithBuyer.length > 0 && "Other "}
            Opportunities
          </Text>
          {renderOpportunityList(opportunitiesWithoutBuyer)}
        </OtherOpportunitiesSection>
      )}
    </OpportunitiesListContainer>
  );
}

function OpportunityListItem({
  opportunity,
  entityIds,
  entityType,
  onSave,
}: {
  opportunity: OpportunityWithSavedStatus;
  entityIds: string[];
  entityType: EntityType;
  onSave?: () => void;
}) {
  const { isSaved } = opportunity;

  const { mutate: addEntities, isLoading: isAddingEntities } = useAddOpportunityEntity({
    onSuccess: (_, variables) => {
      const messageText =
        variables.entities.entities.length > 1
          ? pluralise(variables.entities.entities.length, entityType, undefined)
          : capitalize(entityType);

      toast.message(`${messageText} saved to ${opportunity.name}`, {
        action: (
          <ToastAction objectType="opportunity" url={`${routes.opportunities}/${opportunity.id}`} />
        ),
      });

      onSave?.();
    },
    onError: () => toast.error(`Failed to save ${entityType} to ${opportunity.name}`),
  });

  const { mutate: removeEntities, isLoading: isRemovingEntities } = useRemoveOpportunityEntity({
    onSuccess: () => {
      const messageText =
        entityIds.length > 1
          ? pluralise(entityIds.length, entityType, undefined)
          : capitalize(entityType);

      toast.message(`${messageText} removed from ${opportunity.name}`, {
        action: (
          <ToastAction objectType="opportunity" url={`${routes.opportunities}/${opportunity.id}`} />
        ),
      });

      onSave?.();
    },
    onError: () => toast.error(`Failed to remove ${entityType} from ${opportunity.name}`),
  });

  const isLoading = isAddingEntities || isRemovingEntities;

  const onToggleSave = () => {
    if (isSaved) {
      // If saved, use the bulk remove functionality
      removeEntities({
        id: opportunity.id,
        entities: {
          entities: entityIds.map((id) => ({
            entityId: id,
            entityType: convertEntityTypeToOpportunityEntityType(entityType, OpportunityEntityType),
          })),
        },
      });
    } else {
      // If not saved, add all entities - our backend now handles duplicates
      addEntities({
        id: opportunity.id,
        entities: {
          entities: entityIds.map((id) => ({
            entityId: id,
            entityType: convertEntityTypeToOpportunityEntityType(entityType, OpportunityEntityType),
          })),
        },
      });
    }
  };

  const renderBuyerText = () => (
    <Text key={opportunity.buyers[0].id} description ellipsis color="sysTextSecondary">
      {opportunity.buyers[0].name}{" "}
      {opportunity.buyers.length > 1 &&
        `& ${pluralise(opportunity.buyers.length - 1, "buyer", undefined, "more")}`}
    </Text>
  );

  return (
    <OpportunityItem key={opportunity.id}>
      <ContentFlex>
        <Flex column minWidth={0}>
          <Text ellipsis>{opportunity.name}</Text>
          <Text description ellipsis color="sysTextSecondary">
            {opportunity.description}
          </Text>
          {opportunity.buyers.length > 0 && renderBuyerText()}
        </Flex>
        {isSaved && <BookmarkIcon icon="bookmark" color="sysPrimaryDefault" size={12} />}
      </ContentFlex>
      <ActionsContainer>
        <Button
          icon="bookmark"
          iconProps={{
            size: 16,
            fill: isSaved ? "currentColor" : "none",
            color: "currentColor",
          }}
          aria-label={
            isSaved
              ? `Remove from opportunity ${opportunity.name}`
              : `Add to opportunity ${opportunity.name}`
          }
          onClick={onToggleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          {isSaved ? "Unsave" : "Save"}
        </Button>
        <Button
          as="a"
          target="_blank"
          buttonType="secondary"
          href={`${routes.opportunities}/${opportunity.id}`}
        >
          View
        </Button>
      </ActionsContainer>
    </OpportunityItem>
  );
}

const ActionsContainer = styled.div({
  display: "none",
  gap: 4,
});

const BookmarkIcon = styled(UIcon)({
  display: "flex",
  alignItems: "center",
  marginLeft: "8px",
});

const OpportunitiesListContainer = styled(InfiniteScroll)({
  display: "flex",
  flexDirection: "column",
  gap: 32,

  height: "inherit",
  overflow: "auto",
});

const MatchingBuyerOpportunitiesSection = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 8,

  padding: 16,
  backgroundColor: theme.colors.sysPrimarySubtle,
  borderRadius: 4,
}));

const OtherOpportunitiesSection = styled(Flex)({
  flexDirection: "column",
  gap: 8,
});

const OpportunityItem = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  boxSizing: "content-box",
  gap: 16,
  height: 46,
  border: "1px solid",
  borderRadius: 4,
  padding: "12px 16px",
  backgroundColor: theme.colors.sysTextWhite,
  borderColor: theme.colors.sysBorderDecorative,
  alignItems: "center",
  transition: smoothTransition,

  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
    borderColor: theme.colors.sysPrimaryDefault,
    [`${ActionsContainer}`]: {
      display: "flex",
    },
    [`${BookmarkIcon}`]: {
      display: "none",
    },
  },
}));

const ContentFlex = styled(Flex)({
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  overflow: "hidden",
  flex: 1,
});
